// components/Admin/SellerManagement/SalesManagement/RegenerateWelcomeSetModal.jsx

import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../../../../components/ui/dialog";
import { Button } from "../../../../components/ui/button";
import { Label } from "../../../../components/ui/label";
import { Input } from "../../../../components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../../../components/ui/table";
import { Badge } from "../../../../components/ui/badge";
import { useToast } from "../../../../hooks/use-toast";
import { Loader2, RefreshCw, CheckCircle2, AlertTriangle } from 'lucide-react';
import { getSellerCategoryPricingOptions, regenerateWelcomeSet } from '../../../../services/AdminApis';
import { cn } from "../../../../lib/utils"; // Assuming you have a utility for class merging

const RegenerateWelcomeSetModal = ({ isOpen, onClose, sellerId, onSuccess }) => {
    const [categories, setCategories] = useState([]);
    const [selectedCategoryId, setSelectedCategoryId] = useState(null);
    const [reason, setReason] = useState("");
    const [loading, setLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const { toast } = useToast();

    useEffect(() => {
        if (isOpen && sellerId) {
            fetchCategories();
            setSelectedCategoryId(null);
            setReason("");
        }
    }, [isOpen, sellerId]);

    const fetchCategories = async () => {
        setLoading(true);
        try {
            const data = await getSellerCategoryPricingOptions(sellerId);
            setCategories(data);
        } catch (error) {
            toast({
                variant: "destructive",
                title: "Error",
                description: "Failed to load pricing options."
            });
        } finally {
            setLoading(false);
        }
    };

    const handleConfirm = async () => {
        if (!selectedCategoryId || !reason) return;

        setSubmitting(true);
        try {
            await regenerateWelcomeSet(sellerId, {
                new_master_category_id: selectedCategoryId,
                reason: reason
            });

            toast({
                title: "Success",
                description: "Welcome set regenerated successfully.",
                className: "bg-green-600 text-white"
            });
            onSuccess();
            onClose();
        } catch (error) {
            toast({
                variant: "destructive",
                title: "Error",
                description: error?.response?.data?.detail || "Failed to regenerate package."
            });
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
            <DialogContent className="sm:max-w-[700px]"> {/* Widen the modal for the table */}
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <RefreshCw className="h-5 w-5 text-blue-600" />
                        Change Welcome Set Basis
                    </DialogTitle>
                    <DialogDescription>
                        Select a category below to re-base the Welcome Set. The new set will contain 10 leads worth of coins based on the selected category's price.
                    </DialogDescription>
                </DialogHeader>

                <div className="grid gap-6 py-4">
                    {/* Category Selection Table */}
                    <div className="border rounded-md max-h-[300px] overflow-y-auto">
                        <Table>
                            <TableHeader className="bg-gray-50 sticky top-0">
                                <TableRow>
                                    <TableHead>Master Category</TableHead>
                                    <TableHead>Local Category</TableHead>
                                    <TableHead className="text-right">Coins / Lead</TableHead>
                                    <TableHead className="w-[100px] text-center">Action</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {loading ? (
                                    <TableRow>
                                        <TableCell colSpan={4} className="h-24 text-center">
                                            <Loader2 className="h-6 w-6 animate-spin mx-auto text-gray-400" />
                                        </TableCell>
                                    </TableRow>
                                ) : categories.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={4} className="h-24 text-center text-muted-foreground">
                                            No categories found for this seller.
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    categories.map((cat) => (
                                        <TableRow 
                                            key={cat.master_category_id}
                                            className={cn(
                                                "cursor-pointer transition-colors",
                                                selectedCategoryId === cat.master_category_id ? "bg-blue-50 border-blue-200" : "hover:bg-gray-50",
                                                !cat.is_configured && "opacity-50 pointer-events-none bg-gray-50"
                                            )}
                                            onClick={() => cat.is_configured && setSelectedCategoryId(cat.master_category_id)}
                                        >
                                            <TableCell className="font-medium">{cat.master_category_name}</TableCell>
                                            <TableCell>{cat.local_category_name}</TableCell>
                                            <TableCell className="text-right font-mono">
                                                {cat.base_coins_per_lead > 0 ? (
                                                    <Badge variant="outline" className="font-bold">
                                                        {cat.base_coins_per_lead}
                                                    </Badge>
                                                ) : (
                                                    <span className="text-gray-400">-</span>
                                                )}
                                            </TableCell>
                                            <TableCell className="text-center">
                                                {selectedCategoryId === cat.master_category_id && (
                                                    <CheckCircle2 className="h-5 w-5 text-blue-600 mx-auto" />
                                                )}
                                                {!cat.is_configured && (
                                                     <div title="Pricing not configured">
                                                        <AlertTriangle className="h-4 w-4 text-amber-500 mx-auto" />
                                                     </div>
                                                )}
                                            </TableCell>
                                        </TableRow>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                    </div>

                    {/* Reason Input */}
                    <div className="grid gap-2">
                        <Label>Reason for Change <span className="text-red-500">*</span></Label>
                        <Input 
                            placeholder="e.g. Seller primary focus is this category..." 
                            value={reason}
                            onChange={(e) => setReason(e.target.value)}
                        />
                    </div>
                </div>

                <DialogFooter>
                    <Button variant="outline" onClick={onClose} disabled={submitting}>Cancel</Button>
                    <Button onClick={handleConfirm} disabled={!selectedCategoryId || !reason || submitting}>
                        {submitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                        Regenerate Set
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

export default RegenerateWelcomeSetModal;